import { Button } from "../components/Button";
import { ContentMessage } from "../components/ContentMessage";
import { Icon } from "../components/Icon";
import { Input } from "../components/Input";
import { PopoverContent, PopoverRoot, PopoverTrigger, } from "../components/Popover";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { Spinner } from "../components/Spinner";
import { CheckDone01, SearchMd, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import React, { forwardRef, useEffect, useRef, useState, } from "react";
export const SearchInput = forwardRef(({ placeholder = "Search", id, value, onChange, onKeyDown, onFocus, onBlur, name, disabled = false, isLoading = false, className, }, ref) => {
    const clearInputField = () => {
        onChange("");
    };
    const inputId = id !== null && id !== void 0 ? id : name;
    return (React.createElement("div", { className: cn("relative", className) },
        React.createElement(Input, { id: inputId, type: "text", name: name, autoComplete: "off", placeholder: placeholder, value: value, onChange: (e) => {
                onChange(e.target.value);
            }, onFocus: onFocus, onBlur: onBlur, onKeyDown: onKeyDown, disabled: disabled, ref: ref }),
        React.createElement("div", { className: "absolute inset-y-0 right-0 flex items-center pr-1" }, isLoading ? (React.createElement("div", { className: "px-1" },
            React.createElement(Spinner, { size: "xs" }))) : value ? (React.createElement(Button, { icon: XClose, variant: "ghost", size: "xs", onClick: clearInputField })) : (React.createElement("div", { className: cn("px-2", disabled ? "text-muted-foreground" : "text-foreground") },
            React.createElement(Icon, { visual: SearchMd, size: "xs", className: "text-muted-foreground" }))))));
});
SearchInput.displayName = "SearchInput";
const MAX_HEIGHT_CLASSES = {
    sm: "max-h-48",
    md: "max-h-72",
    lg: "max-h-96",
    xl: "max-h-[40rem]",
};
function BaseSearchInputWithPopover({ items, renderItem, onItemSelect, onSelectAll, contentClassName, className, open, onOpenChange, value, onChange, mountPortal, mountPortalContainer, noResults, isLoading, contentMessage, displayItemCount = false, totalItems, stickyTopContent, stickyBottomContent, availableHeight = false, maxHeight = "md", ...searchInputProps }, ref) {
    // biome-ignore lint/correctness/useHookAtTopLevel: this is a forwardRef render function, Biome can't trace the forwardRef call
    const [selectedIndex, setSelectedIndex] = useState(0);
    // biome-ignore lint/correctness/useHookAtTopLevel: this is a forwardRef render function
    const itemRefs = useRef([]);
    const showHeader = Boolean(stickyTopContent) ||
        (items.length > 0 && (displayItemCount || onSelectAll));
    const showBottom = Boolean(stickyBottomContent);
    // biome-ignore lint/correctness/useHookAtTopLevel: this is a forwardRef render function
    useEffect(() => {
        itemRefs.current = new Array(items.length).fill(null);
    }, [items.length]);
    // biome-ignore lint/correctness/useHookAtTopLevel: this is a forwardRef render function
    // biome-ignore lint/correctness/useExhaustiveDependencies: items is a prop, resetting index on change is intentional
    useEffect(() => {
        setSelectedIndex(0);
    }, [items]);
    const handleKeyDown = (e) => {
        if (!open || !items.length) {
            return;
        }
        switch (e.key) {
            case "ArrowUp":
                e.preventDefault();
                setSelectedIndex((current) => {
                    var _a;
                    const newIndex = (current - 1 + items.length) % items.length;
                    (_a = itemRefs.current[newIndex]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: "nearest" });
                    return newIndex;
                });
                break;
            case "ArrowDown":
                e.preventDefault();
                setSelectedIndex((current) => {
                    var _a;
                    const newIndex = (current + 1) % items.length;
                    (_a = itemRefs.current[newIndex]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: "nearest" });
                    return newIndex;
                });
                break;
            case "Enter":
                e.preventDefault();
                onOpenChange(false);
                if (items[selectedIndex] && onItemSelect) {
                    onItemSelect(items[selectedIndex]);
                }
                break;
        }
    };
    return (React.createElement(PopoverRoot, { modal: false, open: open, onOpenChange: onOpenChange },
        React.createElement(PopoverTrigger, { asChild: true },
            React.createElement(SearchInput, { ref: ref, className: cn("w-full", className), value: value, onChange: (newValue) => {
                    onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
                    if (newValue && !open) {
                        onOpenChange(true);
                    }
                }, ...searchInputProps, onKeyDown: handleKeyDown, "aria-expanded": open, "aria-haspopup": "listbox", "aria-controls": "search-popover-content" })),
        React.createElement(PopoverContent, { className: cn("w-(--radix-popover-trigger-width) rounded-lg border border-border bg-background shadow-lg", availableHeight &&
                "max-h-(--radix-popover-content-available-height) overflow-hidden", contentClassName), sideOffset: 0, fullWidth: true, align: "start", id: "search-popover-content", onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), onInteractOutside: () => onOpenChange(false), mountPortal: mountPortal, mountPortalContainer: mountPortalContainer },
            React.createElement("div", { className: cn("flex flex-col overflow-hidden rounded-lg", availableHeight && "max-h-full") },
                showHeader && (React.createElement("div", { className: cn("z-10 flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background p-2"), onMouseDown: (e) => e.preventDefault() },
                    React.createElement("div", { className: "flex flex-1 items-center gap-2" },
                        stickyTopContent,
                        displayItemCount && items.length > 0 && (React.createElement("span", { className: "text-sm text-muted-foreground" },
                            items.length,
                            " search results",
                            totalItems && ` (out of ${totalItems})`,
                            "."))),
                    onSelectAll && items.length > 0 && (React.createElement(Button, { variant: "ghost", size: "xs", onClick: onSelectAll, label: "Select all", icon: CheckDone01 })))),
                React.createElement(ScrollArea, { className: cn("flex min-h-0 flex-1 flex-col", availableHeight
                        ? "max-h-[calc(var(--radix-popover-content-available-height)-12px)]"
                        : MAX_HEIGHT_CLASSES[maxHeight]), hideScrollBar: true },
                    React.createElement("div", { role: "listbox", className: "flex flex-col" }, items.length > 0 ? (items.map((item, index) => (React.createElement("div", { key: index, ref: (el) => (itemRefs.current[index] = el), onClick: () => {
                            onOpenChange(false);
                            onItemSelect === null || onItemSelect === void 0 ? void 0 : onItemSelect(item);
                        } }, renderItem(item, selectedIndex === index))))) : isLoading ? (React.createElement("div", { className: "flex justify-center py-8" },
                        React.createElement(Spinner, { variant: "dark", size: "md" }))) : (React.createElement("div", { className: "p-4 text-center text-sm italic text-muted-foreground" }, noResults !== null && noResults !== void 0 ? noResults : ""))),
                    React.createElement(ScrollBar, { className: "py-0" })),
                showBottom && (React.createElement("div", { className: cn("z-10 hidden shrink-0 items-center justify-between gap-2 border-t border-border bg-background p-2 sm:flex") }, stickyBottomContent)),
                contentMessage && (React.createElement("div", { className: "p-1" },
                    React.createElement(ContentMessage, { ...contentMessage })))))));
}
export const SearchInputWithPopover = forwardRef(BaseSearchInputWithPopover);
//# sourceMappingURL=SearchInput.js.map